import { useCallback, useRef, useState } from 'react';
import classNames from 'classnames';

import Container from '@ui/container/container';
import CreatableReactSelect from '@ui/select/react-select/creatable-react-select';
import { reactSelectAdminStyles } from '@ui/select/react-select/react-select-styles';
import { ReactSelectInstance, TReactSelectOption } from '@ui/select/react-select/custom-react-select.types';
import Text from '@ui/text/text/text';
import useTrackReactSelectInputDimensions from '@ui/select/react-select/helpers/use-track-react-select-input-dimensions';
import OptionalComponent from '@ui/optional-component/optional-component';

import styles from './google-meet-firesides.module.css';

const MIN_INPUT_HEIGHT = 36;

type TGoogleMeetFiresidesProps = {
  setHostEmails: (emails: TReactSelectOption<string, string>[]) => void;
  hostEmails: TReactSelectOption<string, string>[];
  hideMenu?: boolean;
};

const GoogleMeetFiresides = ({ hostEmails, setHostEmails, hideMenu = true }: TGoogleMeetFiresidesProps) => {
  const selectRef = useRef<ReactSelectInstance>(null);

  const [dirty, setDirty] = useState(false);

  const inputDimensions = useTrackReactSelectInputDimensions(selectRef);

  const handleScrollAfterCreate = useCallback((scrollTo: 'left' | 'top') => {
    setTimeout(() => {
      const select = selectRef.current;
      if (!select) return;
      const controlRef = select?.controlRef;
      if (!controlRef) return;
      const valueContainer = controlRef.querySelector('.fireside-hosts-react-select__value-container');
      if (!valueContainer) return;
      const scrollAmount = scrollTo === 'left' ? valueContainer.scrollWidth : valueContainer.scrollHeight;

      valueContainer.scrollTo({
        [scrollTo]: scrollAmount,
        behavior: 'smooth',
      });
    }, 0);
  }, []);

  const isMultiLine = (inputDimensions?.height || 0) > MIN_INPUT_HEIGHT;

  const getStyles = useCallback(() => {
    return reactSelectAdminStyles({
      control: () => ({
        paddingLeft: 0,
        minHeight: `${MIN_INPUT_HEIGHT}px`,
        '> div:first-of-type': {
          alignItems: 'flex-start',
        },
        borderRadius: isMultiLine ? '12px' : '100px',
        ...(dirty && !hostEmails.length
          ? {
              border: '1px solid var(--semantic-red-500)',
              '&:hover': {
                borderColor: 'var(--semantic-red-500)',
              },
              '&:focus-within': {
                borderColor: 'var(--semantic-red-500) !important',
              },
            }
          : {
              border: '1px solid var(--gray-600)',
              '&:hover': {
                border: '1px solid var(--gray-500)',
              },
              '&:focus-within': {
                border: '1px solid var(--gray-500) !important',
              },
            }),
      }),
      option: () => ({ fontSize: '12px' }),
      valueContainer: () => ({ borderRadius: 'inherit', maxHeight: '60px', overflowY: 'auto' }),
      multiValue: () => ({ height: '22px', margin: '3px', border: '1px solid var(--gray-500)' }),
      dropdownIndicator: () => ({ display: 'none' }),
      placeholder: () => ({ height: '21px', color: 'var(--gray-500)' }),
    });
  }, [dirty, hostEmails.length, isMultiLine]);

  return (
    <Container
      background="var(--gray-900)"
      width="100%"
      height="100%"
      padding="16px"
      borderRadius="12px"
      className="flex-column"
      data-component="GoogleMeetFiresides"
    >
      <div className="flex jc-space-between padding-l-14 padding-r-14">
        <Text color="var(--gray-400)" className="L2 margin-b-2">
          Hosts
        </Text>
        <OptionalComponent display={dirty && !hostEmails.length}>
          <Text className="C1" color="var(--semantic-red-500)">
            Please enter at least 1 email
          </Text>
        </OptionalComponent>
      </div>

      <CreatableReactSelect
        // Important: do not use menuIsOpen={false} when hiding menu or react-select is no longer able to create new items
        // we use the `display-none` class below to simply hide it
        isClearable
        isMulti
        value={hostEmails}
        ref={selectRef}
        classNamePrefix="fireside-hosts-react-select"
        onBlur={() => {
          setDirty(true);
        }}
        onChange={(options, meta) => {
          if (!Array.isArray(options)) return;
          // very simple email regex that just looks for no space string, then @ then no space string, then a dot, then no space string
          // but is allowed to start or end with empty string
          const validEmails = options
            .filter((option) => {
              const trimmedValue = option.value.trim();
              const emailRegex = /^[^\s]+@[^\s]+\.[^\s]+$/;
              return emailRegex.test(trimmedValue);
            })
            .map((option) => {
              return {
                label: option.label.trim(),
                value: option.value.trim(),
              };
            });
          setHostEmails(validEmails);

          const isNewOption = meta?.action === 'create-option';
          if (isNewOption) {
            handleScrollAfterCreate('top');
          }
        }}
        placeholder="Enter host emails *"
        classNames={{
          control: () => `C2 ${styles.control} ${isMultiLine ? styles.showScrollbar : ''}`,
          multiValueLabel: () => 'C2',
          menu: () => classNames('S2', { 'display-none': hideMenu }),
          input: () => 'S2',
          placeholder: () => 'S3 flex ai-flex-end',
          noOptionsMessage: () => 'S3',
          option: () => 'S3',
        }}
        noOptionsMessage={() => 'Type and press enter to add an email'}
        styles={getStyles()}
      />
    </Container>
  );
};

export default GoogleMeetFiresides;
